import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const detail = (e) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : 'Une erreur est survenue. Réessayez.'; };

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [state, setState] = useState('checking'); // checking | valid | invalid | done
  const [emailMasked, setEmailMasked] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); setError('Lien incomplet.'); return; }
    axios.get(`${API}/api/auth/password-setup/${encodeURIComponent(token)}`)
      .then((r) => { setEmailMasked(r.data.email_masked); setState('valid'); })
      .catch((e) => { setError(detail(e)); setState('invalid'); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pwd.length < 8) return setError('8 caractères minimum.');
    if (pwd !== pwd2) return setError('Les deux mots de passe ne correspondent pas.');
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/password-setup/${encodeURIComponent(token)}`, { password: pwd });
      setState('done');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) { setError(detail(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="set-password-page">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl"><Lock className="h-5 w-5 text-emerald-600" />Définir mon mot de passe</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === 'checking' && <p className="text-sm text-slate-500" data-testid="set-password-checking">Vérification du lien…</p>}
          {state === 'invalid' && (
            <div className="space-y-3" data-testid="set-password-invalid">
              <p className="text-sm text-red-700 flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5" />{error}</p>
              <Link to="/login"><Button variant="outline" className="w-full">Retour à la connexion</Button></Link>
            </div>
          )}
          {state === 'valid' && (
            <form onSubmit={submit} className="space-y-3" data-testid="set-password-form">
              <p className="text-sm text-slate-600">Compte : <strong data-testid="set-password-email">{emailMasked}</strong>. Ce lien est à usage unique.</p>
              <div><Label htmlFor="pwd">Nouveau mot de passe</Label><Input id="pwd" type="password" autoComplete="new-password" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="set-password-input" /></div>
              <div><Label htmlFor="pwd2">Confirmer</Label><Input id="pwd2" type="password" autoComplete="new-password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} data-testid="set-password-confirm-input" /></div>
              {error && <p className="text-sm text-red-700" data-testid="set-password-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="set-password-submit">{busy ? 'Enregistrement…' : 'Enregistrer et me connecter'}</Button>
            </form>
          )}
          {state === 'done' && (
            <p className="text-sm text-emerald-700 flex items-center gap-2" data-testid="set-password-done"><CheckCircle2 className="h-4 w-4" />Mot de passe enregistré. Redirection vers la connexion…</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
